import React, { useEffect, useState, Dispatch, SetStateAction } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom"; // Import navigation hook
import {
  ClinicalFormInfo,
  MemberAnswerInfo,
  MemberFormInfo,
  MemberFormStatus,
} from "clinical-forms-experience-client";
import {
  getAllClinicalForms,
  getAllMemberForms,
  getAllMemberFormsStatuses,
  useAssessmentParams,
} from "./apiUtils.assessment";
import { ClinicalFormsConstants } from "../ClinicalForms/ClinicalFormsConstants";
import { navigateToAssessment } from "./utils.assessment";

const statusColorMap: Map<string, string> = new Map();
statusColorMap.set(ClinicalFormsConstants.incompleteStatus, "incomplete");
statusColorMap.set(ClinicalFormsConstants.draftStatus, "draft");
statusColorMap.set(ClinicalFormsConstants.submittedStatus, "submitted");
statusColorMap.set(ClinicalFormsConstants.dueSoonStatus, "due-soon");
statusColorMap.set(ClinicalFormsConstants.overdueStatus, "overdue");
statusColorMap.set(ClinicalFormsConstants.dueTodayStatus, "due-today");

const displayStatusMap: Map<string, string> = new Map();
displayStatusMap.set(ClinicalFormsConstants.incompleteStatus, "Incomplete");
displayStatusMap.set(ClinicalFormsConstants.draftStatus, "Draft");
displayStatusMap.set(ClinicalFormsConstants.submittedStatus, "Completed");
displayStatusMap.set(ClinicalFormsConstants.dueSoonStatus, "Due Soon");
displayStatusMap.set(ClinicalFormsConstants.overdueStatus, "Due");
displayStatusMap.set(ClinicalFormsConstants.dueTodayStatus, "Due Today");

const AssessmentSideNavBar = ({
  memberForm,
  setAllAnswersGiven,
}: {
  memberForm: MemberFormInfo | null;
  setAllAnswersGiven?: Dispatch<SetStateAction<boolean>>;
}) => {
  const [clinicalForms, setClinicalForms] = useState<ClinicalFormInfo[]>([]);
  const [memberForms, setMemberForms] = useState<MemberFormInfo[]>([]);
  const [assessmentStatus, setAssessmentStatus] = useState<
    Map<string, { status: string; dueDays?: number | null }>
  >(new Map());
  const { patientId, assessmentTypeId, editPgis } = useAssessmentParams();
  const navigate = useNavigate(); // Initialize navigation hook
  const TooltipWrapper = ({
    status,
    color,
    dueDays,
  }: {
    status: string;
    color: string;
    dueDays?: null | number;
  }) => {
    const displayValue =
      dueDays != null ? `${status}: ${dueDays}d` : `${status}`;
    return (
      <OverlayTrigger
        trigger={["hover", "focus"]}
        placement="right"
        overlay={<Tooltip>{displayValue}</Tooltip>}
      >
        <div className={`dot-status status-${color}`}></div>
      </OverlayTrigger>
    );
  };

  const startAssessment = (formId: string) => {
    if (editPgis) return;
    navigateToAssessment(
      formId,
      assessmentTypeId!,
      patientId!,
      setAllAnswersGiven!,
      navigate
    );
  };

  const getClinicalForms = async () => {
      const formsRespnse = await getAllClinicalForms();
      setClinicalForms(formsRespnse?.data || []);
  }

  useEffect(() => {
    getClinicalForms()
  }, [])
  
  useEffect(() => {
    const loadAssessmentStatuses = async () => {
      try {
        const memberForms = await getAllMemberForms(patientId!);
        setMemberForms(memberForms?.data || []);
        const formStatuses = new Map(
          clinicalForms.map((assessment) => {
            const memberForm = memberForms?.data?.find(
              (form) => form.formId === assessment.id
            );
            const status = memberForm
              ? { status: memberForm.status, dueDays: null }
              : { status: ClinicalFormsConstants.incompleteStatus, dueDays: null };
            return [assessment.id, status];
          })
        );
        setAssessmentStatus(formStatuses);

      } catch (error) {
        console.error("Failed to load assessment statuses", error);
      }
    };
    loadAssessmentStatuses();
  }, [clinicalForms, memberForm]);

  const getFormStatus = (formId: string) => {
    const status = assessmentStatus.get(formId)?.status;
    if (status === ClinicalFormsConstants.discardedStatus) {
      const pastmemberForms = memberForms?.filter(
        (form) => form.formId === formId && form.status !== ClinicalFormsConstants.discardedStatus);
      if (pastmemberForms?.length) {
        return displayStatusMap.get(pastmemberForms[0].status);
      } else {
        return displayStatusMap.get(ClinicalFormsConstants.incompleteStatus);
      }
    } else {
      return displayStatusMap.get(assessmentStatus.get(formId)?.status || "")
    }
  }
  const getFormStatusColor = (formId: string) => {
    const status = assessmentStatus.get(formId)?.status;
    if (status === ClinicalFormsConstants.discardedStatus) {
      const pastmemberForms = memberForms?.filter(
        (form) => form.formId === formId && form.status !== ClinicalFormsConstants.discardedStatus);
      if (pastmemberForms?.length) {
        return statusColorMap.get(pastmemberForms[0].status);
      } else {
        return statusColorMap.get(ClinicalFormsConstants.incompleteStatus);
      }
    } else {
      return statusColorMap.get(assessmentStatus.get(formId)?.status || "")
    }
  }

  return (
    <div className="assessment__aside ">
      <div className="text-light fs-11 text-uppercase mb-1">Assessment</div>
      <h6 className="fw-semibold mb-3">List of Assessments</h6>
      <ul className="list-unstyled mb-0 d-flex flex-column gap-1">
        {clinicalForms?.map((form) => {
          const isSelected = form.id === assessmentTypeId;
          return (
            <li key={form.id}>
              <button
                type="button"
                onClick={() => startAssessment(form.id)}
                disabled={editPgis}
                className={`assessment-nav-item w-100 text-start fs-13 px-3 py-2 rounded-2 d-flex align-items-center justify-content-between ${
                  isSelected ? "assessment-nav-item--active fw-semibold" : ""
                }`}
              >
                <span className="flex-fill me-2">{form.title}</span>
                <span className="flex-shrink-0 align-self-start mt-1">
                  <TooltipWrapper
                    status={getFormStatus(form.id) || ""}
                    color={getFormStatusColor(form.id) || ""}
                    dueDays={assessmentStatus.get(form.id)?.dueDays}
                  />
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AssessmentSideNavBar;